/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { ReplyOutlined } from '@material-ui/icons';
import { useParams, Link, useHistory } from 'react-router-dom';

import useStyles from './style';
import { getTask } from '../../../../requests/api/task';
import { getSurveysByType, editDetailSurvey } from '../../../../requests/api/survey';
import {
  ENTRENAMIENTO,
  CHECK_IN,
  PRODUCT,
  GENERAL,
  REQUEST,
} from '../../../../utils/const/formTypes';
import { getLabelOptions } from '../../../../utils/date';
import Select from '../../../../components/Select';
import Snackbar from '../../../../components/Snackbar';
import sleep from '../../../../utils/sleep';

const TDStringKeys = {
  id: 'id',
  company: 'company',
  project: 'project',
  title: 'title',
  type: 'type',
  label: 'label',
  requiredGeoreference: 'requiredGeoreference',
  maxDistance: 'maxDistance',
  revisionPolicy: 'revisionPolicy',
  needReview: 'needReview',
  executionSurveyId: 'executionSurveyId',
  reviewSurveyId: 'reviewSurveyId',
  aiAnalysisRequired: 'aiAnalysisRequired',
  verticalForm: 'verticalForm',
  teleaudit: 'teleaudit',
  switchStock: 'switchStock',
  description: 'description',
};

const TaskEditDetail = () => {
  const taskURLPath = '/tasks';
  const { t } = useTranslation();
  const classes = useStyles();
  const { taskId } = useParams();
  const history = useHistory();

  const labels = {
    [TDStringKeys.id]: 'ID',
    [TDStringKeys.company]: t('taskDetailComponent.tableHeaders.companyName'),
    [TDStringKeys.project]: t('taskDetailComponent.tableHeaders.projectName'),
    [TDStringKeys.type]: t('taskDetailComponent.tableHeaders.type'),
    [TDStringKeys.label]: t('taskDetailComponent.tableHeaders.label'),
    [TDStringKeys.requiredGeoreference]: t('taskDetailComponent.tableHeaders.requiredGeoreference'),
    [TDStringKeys.maxDistance]: t('taskDetailComponent.tableHeaders.maxDistance'),
    [TDStringKeys.revisionPolicy]: t('taskDetailComponent.tableHeaders.revisionPolicy'),
    [TDStringKeys.needReview]: t('taskDetailComponent.tableHeaders.needReview'),
    [TDStringKeys.executionSurveyId]: t('taskDetailComponent.tableHeaders.executionSurvey'),
    [TDStringKeys.reviewSurveyId]: t('taskDetailComponent.tableHeaders.reviewSurvey'),
    [TDStringKeys.aiAnalysisRequired]: t('taskDetailComponent.tableHeaders.aiAnalysisRequired'),
    [TDStringKeys.verticalForm]: t('taskDetailComponent.tableHeaders.verticalForm'),
    [TDStringKeys.teleaudit]: t('taskDetailComponent.tableHeaders.teleaudit'),
    [TDStringKeys.switchStock]: t('taskDetailComponent.tableHeaders.switchStock'),
    [TDStringKeys.description]: t('taskDetailComponent.tableHeaders.description'),
  };

  const [taskDetail, setTaskDetail] = useState({
    [TDStringKeys.id]: null,
    [TDStringKeys.company]: null,
    [TDStringKeys.project]: null,
    [TDStringKeys.title]: '',
    [TDStringKeys.type]: '',
    [TDStringKeys.label]: '',
    [TDStringKeys.requiredGeoreference]: false,
    [TDStringKeys.maxDistance]: null,
    [TDStringKeys.revisionPolicy]: false,
    [TDStringKeys.needReview]: '',
    [TDStringKeys.executionSurveyId]: null,
    [TDStringKeys.reviewSurveyId]: null,
    [TDStringKeys.aiAnalysisRequired]: false,
    [TDStringKeys.verticalForm]: false,
    [TDStringKeys.teleaudit]: false,
    [TDStringKeys.switchStock]: false,
    [TDStringKeys.description]: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const [executionSurveyOptions, setExecutionSurveyOptions] = useState([]);
  const [reviewSurveyOptions, setReviewSurveyOptions] = useState([]);

  const showErrorLoad = useCallback(() => {
    setSeverity('error');
    setMessage(t('common.failedLoad'));
  }, [t]);

  useEffect(() => {
    const getTaskById = async () => {
      const { data, success } = await getTask(taskId);
      if (success) {
        const taskData = data.data.task;
        setTaskDetail({
          [TDStringKeys.id]: taskData.id,
          [TDStringKeys.title]: taskData.title,
          [TDStringKeys.company]: { ...taskData.Project.Company },
          [TDStringKeys.project]: taskData.Project.projectName,
          [TDStringKeys.type]: taskData.type,
          [TDStringKeys.label]: taskData.label || '',
          [TDStringKeys.requiredGeoreference]: taskData.requiredGeoreference,
          [TDStringKeys.maxDistance]: taskData.maxDistance,
          [TDStringKeys.revisionPolicy]: taskData.revisionPolicy,
          [TDStringKeys.needReview]: taskData.needReview || '',
          [TDStringKeys.executionSurveyId]: taskData.executionSurveyId || null,
          [TDStringKeys.reviewSurveyId]: taskData.reviewSurveyId || null,
          [TDStringKeys.aiAnalysisRequired]: taskData.aiAnalysisRequired,
          [TDStringKeys.verticalForm]: taskData.vertical,
          [TDStringKeys.teleaudit]: taskData.surveyTeleaudit === true
            || taskData.surveyTeleaudit === 'yes',
          [TDStringKeys.switchStock]: taskData.switchStock,
          [TDStringKeys.description]: taskData.description || '',
        });
        setLoaded(true);
      } else {
        setLoadFailed(true);
        showErrorLoad();
      }
    };

    if (taskId) {
      getTaskById().catch(() => {
        setLoadFailed(true);
        showErrorLoad();
      });
    }
  }, [showErrorLoad, taskId]);

  useEffect(() => {
    if (taskDetail.type !== REQUEST) return undefined;

    const getSurveyOptions = async () => {
      const [executionResponse, reviewResponse] = await Promise.all([
        getSurveysByType('execution'),
        getSurveysByType('review'),
      ]);
      setExecutionSurveyOptions([
        { name: t('task.taskEdition.executionSurveyPlaceholder'), value: '' },
        ...(executionResponse.data?.data?.surveys || []).map((survey) => ({
          name: survey.title,
          value: survey.id,
        })),
      ]);
      setReviewSurveyOptions([
        { name: t('task.taskEdition.reviewSurveyPlaceholder'), value: '' },
        ...(reviewResponse.data?.data?.surveys || []).map((survey) => ({
          name: survey.title,
          value: survey.id,
        })),
      ]);
    };

    getSurveyOptions().catch(showErrorLoad);
    return undefined;
  }, [showErrorLoad, t, taskDetail.type]);

  const parseValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
  };

  const handleSwitchChange = (key) => {
    setTaskDetail((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleOnChangeArg = (event, key) => {
    setTaskDetail((prev) => ({
      ...prev,
      [key]: event.target.value,
    }));
  };

  const handleSave = async () => {
    const isInvalidMaxDistance = taskDetail.requiredGeoreference
      && (taskDetail.maxDistance === ''
        || taskDetail.maxDistance === null
        || Number.isNaN(Number(taskDetail.maxDistance))
        || Number(taskDetail.maxDistance) < 0);
    if (isInvalidMaxDistance) {
      setSeverity('error');
      setMessage(t('task.saveErrors.maxDistanceInvalid'));
      return;
    }

    const isRequest = taskDetail.type === REQUEST;
    const maxDistance = taskDetail.requiredGeoreference
      && taskDetail.maxDistance !== ''
      && taskDetail.maxDistance !== null
      ? Number.parseInt(taskDetail.maxDistance, 10)
      : null;
    const body = {
      id: parseInt(taskId, 10),
      title: taskDetail.title,
      type: taskDetail.type,
      requiredGeoreference: taskDetail.requiredGeoreference,
      maxDistance,
      revisionPolicy: taskDetail.revisionPolicy,
      label: taskDetail.label,
      aiAnalysisRequired: taskDetail.aiAnalysisRequired,
      vertical: taskDetail.verticalForm,
      needReview: isRequest ? taskDetail.needReview : 'no',
      executionSurveyId: isRequest ? taskDetail.executionSurveyId : null,
      reviewSurveyId: isRequest ? taskDetail.reviewSurveyId : null,
      switchStock: taskDetail.switchStock,
      surveyTeleaudit: taskDetail.teleaudit,
      description: taskDetail.description,
    };
    const resp = await editDetailSurvey(body);
    if (resp.success) {
      setMessage(t('common.savedSuccessfully'));
      setSeverity('success');
      sleep(1000).then(() => {
        history.push(`${taskURLPath}/${taskId}`);
      });
    } else {
      setMessage(t('common.savedUnsuccessfully'));
      setSeverity('error');
    }
  };

  const createSelectOptions = () => [
    ENTRENAMIENTO,
    CHECK_IN,
    PRODUCT,
    GENERAL,
    REQUEST,
  ].map((type) => ({
    name: type,
    value: type,
  }));

  const confirmationOptions = [
    { name: t('task.taskEdition.needConfirmation.placeholder'), value: '' },
    { name: t('task.taskEdition.needConfirmation.applicantReview'), value: 'applicationReview' },
    { name: t('task.taskEdition.needConfirmation.otherReview'), value: 'otherReview' },
    { name: t('task.taskEdition.needConfirmation.no'), value: 'no' },
  ];

  const revisionOptions = [
    { name: t('task.taskEdition.revisionPolicy.placeholder'), value: '' },
    { name: t('task.taskEdition.revisionPolicy.yes'), value: 'yes' },
    { name: t('task.taskEdition.revisionPolicy.no'), value: 'no' },
    { name: t('task.taskEdition.revisionPolicy.random'), value: 'random' },
  ];

  return (
    <>
      {loaded ? (
        <div className={classes.content}>
          <div className={classes.headerContainer}>
            <div className={classes.header}>
              <IconButton
                id="backButton"
                aria-label="back"
                className={classes.backButton}
                component={Link}
                to={`${taskURLPath}/${taskId}`}
              >
                <ReplyOutlined fontSize="small" />
                <span>{t('groups.actionButtons.back')}</span>
              </IconButton>
              <TextField
                id={TDStringKeys.title}
                className={classes.title}
                value={taskDetail.title}
                inputProps={{ className: classes.title }}
                onChange={(e) => handleOnChangeArg(e, TDStringKeys.title)}
                fullWidth
              />
            </div>
            <div className={classes.buttonsContainer}>
              <Button onClick={handleSave}>
                {t('task.editDetail.save')}
              </Button>
            </div>
          </div>
          <Divider />
          <TableContainer component={Paper}>
            <Table className={classes.table}>
              <TableBody>
                <TableRow key={TDStringKeys.id}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.id}</Typography>
                  </TableCell>
                  <TableCell align="right">{parseValue(taskDetail.id)}</TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.company}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.company}</Typography>
                  </TableCell>
                  <TableCell align="right">{parseValue(taskDetail.company?.name)}</TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.project}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.project}</Typography>
                  </TableCell>
                  <TableCell align="right">{parseValue(taskDetail.project)}</TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.type}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.type}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Select
                      name="type"
                      items={createSelectOptions()}
                      value={taskDetail.type}
                      onChange={(e) => handleOnChangeArg(e, TDStringKeys.type)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.label}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.label}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Select
                      name="survey-label"
                      items={getLabelOptions(t)}
                      value={taskDetail.label}
                      onChange={(e) => handleOnChangeArg(e, TDStringKeys.label)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.requiredGeoreference}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>
                      {labels.requiredGeoreference}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      className={classes.switch}
                      control={(
                        <Switch
                          checked={taskDetail.requiredGeoreference}
                          aria-label={labels.requiredGeoreference}
                          onChange={() => handleSwitchChange(TDStringKeys.requiredGeoreference)}
                          color="primary"
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                {taskDetail.requiredGeoreference && (
                  <TableRow key={TDStringKeys.maxDistance}>
                    <TableCell component="th" scope="row">
                      <Typography className={classes.semiBold}>{labels.maxDistance}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={taskDetail.maxDistance ?? ''}
                        required={taskDetail.requiredGeoreference}
                        inputProps={{ min: 0 }}
                        onChange={(e) => handleOnChangeArg(e, TDStringKeys.maxDistance)}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                )}
                <TableRow key={TDStringKeys.revisionPolicy}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.revisionPolicy}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Select
                      name="requires-revision"
                      items={revisionOptions}
                      value={taskDetail.revisionPolicy}
                      onChange={(e) => handleOnChangeArg(e, TDStringKeys.revisionPolicy)}
                    />
                  </TableCell>
                </TableRow>
                {taskDetail.type === REQUEST && (
                  <>
                    <TableRow key={TDStringKeys.needReview}>
                      <TableCell component="th" scope="row">
                        <Typography className={classes.semiBold}>{labels.needReview}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Select
                          name="need-review"
                          items={confirmationOptions}
                          value={taskDetail.needReview}
                          onChange={(e) => handleOnChangeArg(e, TDStringKeys.needReview)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow key={TDStringKeys.executionSurveyId}>
                      <TableCell component="th" scope="row">
                        <Typography className={classes.semiBold}>
                          {labels.executionSurveyId}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Select
                          name="execution-survey"
                          items={executionSurveyOptions}
                          value={taskDetail.executionSurveyId || ''}
                          onChange={(e) => handleOnChangeArg(e, TDStringKeys.executionSurveyId)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow key={TDStringKeys.reviewSurveyId}>
                      <TableCell component="th" scope="row">
                        <Typography className={classes.semiBold}>
                          {labels.reviewSurveyId}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Select
                          name="review-survey"
                          items={reviewSurveyOptions}
                          value={taskDetail.reviewSurveyId || ''}
                          onChange={(e) => handleOnChangeArg(e, TDStringKeys.reviewSurveyId)}
                        />
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow key={TDStringKeys.aiAnalysisRequired}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>
                      {labels.aiAnalysisRequired}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      className={classes.switch}
                      control={(
                        <Switch
                          checked={taskDetail.aiAnalysisRequired}
                          aria-label={labels.aiAnalysisRequired}
                          onChange={() => handleSwitchChange(TDStringKeys.aiAnalysisRequired)}
                          color="primary"
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.verticalForm}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.verticalForm}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      className={classes.switch}
                      control={(
                        <Switch
                          checked={taskDetail.type === PRODUCT || taskDetail.verticalForm}
                          aria-label={labels.verticalForm}
                          onChange={() => handleSwitchChange(TDStringKeys.verticalForm)}
                          color="primary"
                        />
                      )}
                      disabled={taskDetail.type === PRODUCT}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.teleaudit}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.teleaudit}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      className={classes.switch}
                      control={(
                        <Switch
                          checked={taskDetail.teleaudit}
                          aria-label={labels.teleaudit}
                          onChange={() => handleSwitchChange(TDStringKeys.teleaudit)}
                          color="primary"
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.switchStock}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.switchStock}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <FormControlLabel
                      className={classes.switch}
                      control={(
                        <Switch
                          checked={taskDetail.switchStock}
                          aria-label={labels.switchStock}
                          onChange={() => handleSwitchChange(TDStringKeys.switchStock)}
                          color="primary"
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={TDStringKeys.description}>
                  <TableCell component="th" scope="row">
                    <Typography className={classes.semiBold}>{labels.description}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      value={taskDetail.description}
                      multiline
                      minRows={4}
                      variant="outlined"
                      className={classes.textField}
                      onChange={(e) => handleOnChangeArg(e, TDStringKeys.description)}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : loadFailed ? (
        <div className={classes.circularProgress}>
          <Typography>{t('common.failedLoad')}</Typography>
        </div>
      ) : (
        <div className={classes.circularProgress}>
          <CircularProgress />
        </div>
      )}
      <Snackbar
        open={message !== ''}
        message={message}
        severity={severity}
        onClose={() => setMessage('')}
      />
    </>
  );
};

export default TaskEditDetail;
